import React, { useState } from 'react';

import { Alert, Button, Divider, Stack, Typography } from '@mui/material';

import type { LensCard, LensDataset } from '~/types/leLens';

import type { PanelShaped } from '../panelTypes';

import AddColumn from './AddColumn';
import CalculatedField from './CalculatedField';
import DatasetSql from './DatasetSql';

/**
 * Region 5 of 02_RIGHT_PANEL_SPEC.md's Data tab: the single export C1's
 * panel renders through `LensEditPanel`'s `datasetActionsSlot`. Three text
 * buttons in one row; each opens its inline section below the row, never a
 * modal, and only one is open at a time (matching the approved prototype,
 * right_panel_prototype.html's `S.inline`).
 *
 * A governed dataset carries no SQL a person can edit (the server compiles every card's view
 * straight into an aggregate query against the catalogue table), so Add column and SQL are
 * pointless here; Calculated field still works, since it runs no warehouse call. In their place,
 * a quiet "Switch to custom query" turns the dataset into one the SQL editor below can edit,
 * after a one-sentence confirm naming what changes.
 */

type OpenSection = 'none' | 'addColumn' | 'calculatedField' | 'sql';

export type DatasetActionsProps = {
  lensId: string;
  dataset: LensDataset;
  /** Cards on the board that read this dataset, for the guard's card counts and diffs. */
  cards: ReadonlyArray<LensCard>;
  /** This dataset's rows, already loaded for the Lens, for Calculated field's live preview. */
  loadedRows: PanelShaped | undefined;
  /** `access.canEdit`: the guard endpoints 403 anyone else server-side (confirmed against
   *  checksController.ts), so Add column and SQL-save are hidden rather than shown broken.
   *  Calculated field has no such gate: it runs no warehouse call. */
  canEdit: boolean;
  /** D7: Apply anyway on a grain refusal, dashboard owner only. Same value as `canEdit` today. */
  isOwner: boolean;
  ownerEmail: string;
  /** Persists one changed dataset (a new column, a calculated field, an edited SQL, or the
   *  governed-to-custom switch) through the existing lens update mutation; the mount site holds
   *  the full Lens and its version. */
  onSaveDataset: (nextDataset: LensDataset) => Promise<void>;
  /** F4/F8's refusal, primary action: opens the New dataset composer with this SQL prefilled. */
  onNewDatasetInstead: (candidateSql: string) => void;
};

export default function DatasetActions({
  canEdit,
  cards,
  dataset,
  isOwner,
  lensId,
  loadedRows,
  onNewDatasetInstead,
  onSaveDataset,
  ownerEmail,
}: DatasetActionsProps) {
  const [open, setOpen] = useState<OpenSection>('none');
  const [confirmingSwitch, setConfirmingSwitch] = useState(false);
  const [switching, setSwitching] = useState(false);
  const isGoverned = dataset.source?.kind === 'governed';

  const toggle = (section: OpenSection) => setOpen((current) => (current === section ? 'none' : section));

  const switchToCustomQuery = async () => {
    setSwitching(true);
    try {
      await onSaveDataset({ ...dataset, source: { ...dataset.source, kind: 'sql' } });
      setConfirmingSwitch(false);
      setOpen('sql');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" divider={<Divider flexItem orientation="vertical" />} spacing={1}>
        {!isGoverned && (
          <Button
            onClick={() => toggle('addColumn')}
            size="small"
            variant={open === 'addColumn' ? 'contained' : 'text'}
          >
            + Add column
          </Button>
        )}
        <Button
          onClick={() => toggle('calculatedField')}
          size="small"
          variant={open === 'calculatedField' ? 'contained' : 'text'}
        >
          + Calculated field
        </Button>
        {!isGoverned && (
          <Button onClick={() => toggle('sql')} size="small" variant={open === 'sql' ? 'contained' : 'text'}>
            SQL
          </Button>
        )}
      </Stack>

      {isGoverned &&
        (confirmingSwitch ? (
          <Alert
            action={
              <Stack direction="row" spacing={0.5}>
                <Button disabled={switching} onClick={switchToCustomQuery} size="small">
                  {switching ? 'Switching…' : 'Switch'}
                </Button>
                <Button disabled={switching} onClick={() => setConfirmingSwitch(false)} size="small">
                  Cancel
                </Button>
              </Stack>
            }
            severity="warning"
            variant="outlined"
          >
            <Typography variant="body2">
              This turns {dataset.name} into a query you maintain yourself. Cards keep working.
            </Typography>
          </Alert>
        ) : (
          <Button onClick={() => setConfirmingSwitch(true)} size="small" sx={{ alignSelf: 'flex-start', ml: -0.5 }}>
            Switch to custom query
          </Button>
        ))}

      {open === 'addColumn' && !isGoverned && (
        <AddColumn
          canEdit={canEdit}
          cards={cards}
          dataset={dataset}
          isOwner={isOwner}
          lensId={lensId}
          onApply={onSaveDataset}
          onNewDatasetInstead={onNewDatasetInstead}
          ownerEmail={ownerEmail}
        />
      )}

      {open === 'calculatedField' && (
        <CalculatedField dataset={dataset} loadedRows={loadedRows} onSave={onSaveDataset} />
      )}

      {open === 'sql' && (
        <DatasetSql
          canEdit={canEdit}
          cards={cards}
          dataset={dataset}
          isOwner={isOwner}
          lensId={lensId}
          onApply={onSaveDataset}
          onNewDatasetInstead={onNewDatasetInstead}
          ownerEmail={ownerEmail}
        />
      )}
    </Stack>
  );
}
